import { useEffect, useState } from 'react'
import { Order, OrderFieldConfig, OrderDirection } from '.'
import { useIntl } from 'react-intl'

export interface UseOrderManagerParams<TField extends string> {
  readonly defaultField: TField
  readonly orderFieldConfig: OrderFieldConfig<TField>
}

const isLocaleSupported = (
  supportedLocales: string[] | undefined,
  locale: string
) => {
  return !supportedLocales || supportedLocales.indexOf(locale) >= 0
}

/**
 * Get derived state for this component is intended to filter out the "selected" or previously "selected"
 * order criteria if its not supported. If it is not supported based on locale or an invalid selection
 * due to change in orderFieldConfig, return the default order.
 * @param nextProps new props
 * @param prevState prev state
 */
const getDerivedStateFromProps = (
  locale: string,
  nextProps: UseOrderManagerParams<any>,
  prevStateOrder
) => {
  const existingOrder: Order<any> = prevStateOrder ? prevStateOrder : undefined

  let newOrder: Order<any>
  const { defaultField, orderFieldConfig } = nextProps

  // eslint-disable-next-line unicorn/prefer-ternary
  if (orderFieldConfig[existingOrder?.field]) {
    newOrder = handleExisting(
      locale,
      orderFieldConfig,
      existingOrder,
      prevStateOrder,
      defaultField
    )
  } else {
    newOrder = {
      field: defaultField,
      direction: orderFieldConfig[defaultField].direction
    }
  }

  return newOrder
}

const handleExisting = (
  locale: string,
  orderFieldConfig: OrderFieldConfig<any>,
  existingOrder: Order<any>,
  prevStateOrder: any,
  defaultField: any
) => {
  const supportedLocales: string[] | undefined =
    orderFieldConfig[existingOrder.field].supportedLocales

  if (isLocaleSupported(supportedLocales, locale)) {
    return prevStateOrder
  }

  const supportedDefaultField = findSupportedDefaultField(
    orderFieldConfig,
    defaultField,
    locale
  )

  return {
    field: supportedDefaultField,
    direction: orderFieldConfig[supportedDefaultField].direction
  }
}

const findSupportedDefaultField = (
  orderFieldConfig: OrderFieldConfig<any>,
  defaultField: any,
  locale: string
) => {
  let supportedDefaultField: string = defaultField

  if (
    !isLocaleSupported(orderFieldConfig[defaultField].supportedLocales, locale)
  ) {
    const fieldKeys: string[] = Object.keys(orderFieldConfig)
    for (const fieldKey of fieldKeys) {
      if (
        isLocaleSupported(orderFieldConfig[fieldKey].supportedLocales, locale)
      ) {
        supportedDefaultField = fieldKey
        break
      }
    }
    console.error(`No supported order field was found for locale '${locale}'.`)
  }

  return supportedDefaultField
}

export const useOrderManager = <TField extends string>(
  params: UseOrderManagerParams<TField>
) => {
  const { locale } = useIntl()

  const [order, setOrder] = useState<Order<TField>>(
    getDerivedStateFromProps(locale, params, undefined)
  )

  useEffect(() => {
    const newOrder = getDerivedStateFromProps(locale, params, order)
    setOrder(newOrder)
  }, [params, locale, order])

  const { orderFieldConfig } = params

  const onSetSortField = (field: TField) => {
    setOrder({
      field,
      direction: orderFieldConfig[field].direction
    })
  }

  const onSetSortDirection = (direction: OrderDirection) => {
    setOrder({
      field: order.field,
      direction
    })
  }

  return {
    order,
    onSetSortField,
    onSetSortDirection
  }
}
